/**
 * Contact messages — what visitors send through the "Contact us" form on vloud.app.
 *
 * Owner and admin only (the messages hold names, addresses and phone numbers). The
 * server enforces that; this page only avoids asking when the answer would be 403.
 *
 * Status flow: new → read → replied (any state can go back to new).
 * Nothing is e-mailed from here: "Reply by email" opens the operator's own mail client.
 */

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { Download, Mail, RefreshCw, Reply, Search, Trash2 } from 'lucide-react';
import {
  AdminApiClient, ApiError, apiErrorText,
  type ContactMessageList, type ContactMessageRow, type ContactStatus, type ContactTopic,
} from '../api';
import { useSession } from '../session';
import { CONTACT_CHANGED_EVENT } from '../components/shell/contactBadge';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

const PAGE_SIZE = 50;

const TOPIC_LABEL: Record<ContactTopic, string> = {
  general: 'General',
  sales: 'Sales',
  support: 'Support',
  partnership: 'Partnership',
  migration: 'Migration help',
  other: 'Other',
};

const STATUS_TABS: Array<{ key: ContactStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'read', label: 'Read' },
  { key: 'replied', label: 'Replied' },
];

function bandFor(status: ContactStatus): Band {
  if (status === 'new') return 'warning';
  if (status === 'replied') return 'healthy';
  return 'neutral';
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function preview(text: string): string {
  const line = (text.split('\n').find((l) => l.trim()) ?? '').trim();
  return line.length > 90 ? `${line.slice(0, 89)}…` : line;
}

export function ContactMessagesPage(): JSX.Element {
  const { state } = useSession();
  const allowed = (state.user?.role === 'owner' || state.user?.role === 'admin') && state.user?.mfa_ok === true;

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  const [status, setStatus] = useState<ContactStatus | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [version, setVersion] = useState(0);
  const [data, setData] = useState<ContactMessageList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Search runs on the server, a moment after the last keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setQ(searchText); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    setLoading(true);
    api.contactMessages({ status, q, limit: PAGE_SIZE, offset })
      .then((r) => { if (alive) { setData(r); setErr(null); } })
      .catch((e) => { if (alive) setErr(apiErrorText(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, allowed, status, q, offset, version]);

  const refresh = useCallback((): void => setVersion((v) => v + 1), []);
  const changed = useCallback((): void => {
    refresh();
    window.dispatchEvent(new Event(CONTACT_CHANGED_EVENT));
  }, [refresh]);

  const open: ContactMessageRow | null = useMemo(
    () => data?.messages.find((m) => m.id === openId) ?? null,
    [data, openId],
  );

  const mark = async (m: ContactMessageRow, next: ContactStatus): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      await api.setContactMessageStatus(m.id, next);
      changed();
    } catch (e) {
      setNotice(`Could not update the message: ${apiErrorText(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: ContactMessageRow): Promise<void> => {
    if (!window.confirm(`Delete the message from ${m.name}? This cannot be undone.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.deleteContactMessage(m.id);
      setOpenId(null);
      changed();
    } catch (e) {
      setNotice(e instanceof ApiError && e.status === 404
        ? 'That message is already gone.'
        : `Could not delete the message: ${apiErrorText(e)}`);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const { blob, filename } = await api.contactMessagesCsv({ status, q });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice(`Could not export: ${apiErrorText(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <section>
        <PageHeader title="Contact messages" icon={Mail} accent="#38bdf8" />
        <p className="muted" role="status">
          Only owners and admins can read contact messages.
        </p>
      </section>
    );
  }

  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + PAGE_SIZE, total);
  const newCount = data?.new_count ?? 0;

  const columns: Column<ContactMessageRow>[] = [
    {
      key: 'received', header: 'Received', width: 170,
      cell: (m) => <span title={m.created_at} style={{ fontWeight: m.status === 'new' ? 600 : 400 }}>{when(m.created_at)}</span>,
    },
    {
      key: 'from', header: 'From',
      cell: (m) => <PrimaryWithSub primary={m.name} sub={m.email} />,
    },
    { key: 'company', header: 'Company', hideOnMobile: true, cell: (m) => m.company ?? '—' },
    { key: 'topic', header: 'Topic', hideOnMobile: true, cell: (m) => TOPIC_LABEL[m.topic] ?? m.topic },
    {
      key: 'message', header: 'Message', hideOnMobile: true,
      cell: (m) => <span className="muted">{preview(m.message)}</span>,
    },
    {
      key: 'status', header: 'Status', width: 110,
      cell: (m) => <StatusChip band={bandFor(m.status)}>{m.status}</StatusChip>,
    },
  ];

  return (
    <section>
      <PageHeader
        title="Contact messages"
        subtitle={`Sent through the Contact us form on vloud.app. ${newCount} new.`}
        icon={Mail}
        accent="#38bdf8"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void exportCsv()} disabled={busy || total === 0}>
              <Download width={14} height={14} />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw width={14} height={14} style={{ animation: loading ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
          </>
        }
      />

      {notice && <p role="alert" className="err" style={{ marginBottom: 12 }}>{notice}</p>}

      <DataTable
        rows={data?.messages ?? null}
        columns={columns}
        rowKey={(m) => m.id}
        onRowClick={(m) => setOpenId(m.id)}
        loading={loading && !data}
        error={err}
        onRetry={refresh}
        emptyTitle={q || status !== 'all' ? 'No messages match' : 'No messages yet'}
        emptyDescription={
          q || status !== 'all'
            ? 'Try another status or search.'
            : 'Messages sent from the Contact us form on vloud.app appear here.'
        }
        toolbar={
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Search width={14} height={14} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                type="search"
                aria-label="Search messages"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search name, email, company or text…"
                style={{
                  padding: '7px 10px 7px 30px', background: 'var(--bg-overlay)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text-primary)', fontSize: '0.8125rem', minWidth: 280, outline: 'none',
                }}
              />
            </div>
            <div role="tablist" aria-label="Status" style={{ display: 'inline-flex', gap: 4 }}>
              {STATUS_TABS.map((t) => (
                <Button
                  key={t.key}
                  role="tab"
                  aria-selected={status === t.key}
                  variant={status === t.key ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => { setStatus(t.key); setOffset(0); }}
                >
                  {t.label}{t.key === 'new' && newCount > 0 ? ` (${newCount})` : ''}
                </Button>
              ))}
            </div>
          </div>
        }
      />

      {total > PAGE_SIZE && (
        <nav aria-label="Pages" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 12 }}>
          <span className="muted">{first}–{last} of {total}</span>
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
          <Button variant="outline" size="sm" disabled={last >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
        </nav>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpenId(null)}
        title={open?.name ?? ''}
        subtitle={open?.email}
        width="lg"
        footer={open && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {open.status !== 'read' && (
                <Button variant="outline" size="sm" disabled={busy} onClick={() => void mark(open, 'read')}>Mark read</Button>
              )}
              {open.status !== 'replied' && (
                <Button size="sm" disabled={busy} onClick={() => void mark(open, 'replied')}>Mark replied</Button>
              )}
              {open.status !== 'new' && (
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void mark(open, 'new')}>Mark new</Button>
              )}
              <a
                className="ui-btn ui-btn--outline ui-btn--size-sm"
                href={`mailto:${open.email}?subject=${encodeURIComponent('Re: your message to Vloud')}`}
              >
                <Reply width={14} height={14} />
                Reply by email
              </a>
            </div>
            <Button variant="destructive" size="sm" disabled={busy} onClick={() => void remove(open)}>
              <Trash2 width={14} height={14} />
              Delete
            </Button>
          </div>
        )}
      >
        {open && (
          <div style={{ display: 'grid', gap: 16 }}>
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', margin: 0 }}>
              <dt className="muted">Status</dt>
              <dd style={{ margin: 0 }}><StatusChip band={bandFor(open.status)}>{open.status}</StatusChip></dd>
              <dt className="muted">Received</dt>
              <dd style={{ margin: 0 }}>{when(open.created_at)}</dd>
              <dt className="muted">Topic</dt>
              <dd style={{ margin: 0 }}>{TOPIC_LABEL[open.topic] ?? open.topic}</dd>
              <dt className="muted">Company</dt>
              <dd style={{ margin: 0 }}>{open.company ?? '—'}</dd>
              <dt className="muted">Phone</dt>
              <dd style={{ margin: 0 }}>{open.phone ?? '—'}</dd>
              {open.read_at && (<><dt className="muted">Read</dt><dd style={{ margin: 0 }}>{when(open.read_at)}</dd></>)}
              {open.replied_at && (<><dt className="muted">Replied</dt><dd style={{ margin: 0 }}>{when(open.replied_at)}</dd></>)}
            </dl>
            <div
              data-testid="contact-message-text"
              style={{
                whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', padding: 14, borderRadius: 10,
                background: 'var(--bg-overlay)', border: '1px solid var(--border)', lineHeight: 1.55,
              }}
            >
              {open.message}
            </div>
          </div>
        )}
      </Drawer>
    </section>
  );
}
